<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import MediaRenderer from '@/components/MediaRenderer.vue'
import UiCta from '@/components/common/UiCta.vue'
import type { CtaOptions } from '@/composables/useCta'
import { localizedPath } from '@/router/i18nPath'
import { useContentStore } from '@/stores/content.store'
import { useFrontpageStore } from '@/stores/frontpage.store'
import { useHeroStore } from '@/stores/hero.store'

const route = useRoute()
const contentStore = useContentStore()
const frontpageStore = useFrontpageStore()
const heroStore = useHeroStore()

const { isLoading: isContentLoading, loading: contentLoading } = storeToRefs(contentStore)
const { isLoading: isFrontpageLoading, loading: frontpageLoading } = storeToRefs(frontpageStore)
const { data } = storeToRefs(heroStore)

// --- derived
const hero = computed(() => data.value?.hero)
const media = computed(() => hero.value?.media)
const settings = computed(() => hero.value?.settings)
const heroHeight = computed(() => cleanString(hero.value?.height))
const routeBaseName = computed(() => String(route.name ?? 'home').replace(/-(en|no)$/, ''))

const isAwaitingApiResponse = computed(() => {
  if (routeBaseName.value === 'home') {
    return (
      isContentLoading.value ||
      !!contentLoading.value.pages ||
      !contentStore.hasLoaded('pages') ||
      isFrontpageLoading.value ||
      !!frontpageLoading.value.frontpage
    )
  }

  if (routeBaseName.value === 'page') {
    return (
      isContentLoading.value ||
      !!contentLoading.value.pages ||
      !contentStore.hasLoaded('pages')
    )
  }

  if (routeBaseName.value === 'post' || routeBaseName.value === 'campaign') {
    return (
      isContentLoading.value ||
      !!contentLoading.value.posts ||
      !contentStore.hasLoaded('posts')
    )
  }

  if (routeBaseName.value === 'gym') {
    return (
      isContentLoading.value ||
      !!contentLoading.value.gyms ||
      !contentStore.hasLoaded('gyms')
    )
  }

  if (routeBaseName.value === 'trainer') {
    return (
      isContentLoading.value ||
      !!contentLoading.value.trainers ||
      !contentStore.hasLoaded('trainers')
    )
  }

  return false
})

const isSuperimposed = computed(() => heroHeight.value === 'full')

const sectionClasses = computed(() => [
  'hero',
  'relative',
  'z-0',
  isSuperimposed.value ? 'superimposed' : '',
  hasHeroMedia.value ? 'has-image' : 'no-image',
  hasHeroMedia.value ? '' : 'bg-gradient-hero',
])

function cleanString(value?: string | null) {
  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  return trimmed || undefined
}

const imageId = computed(() => {
  if (media.value?.type === 'image' && media.value.has_media) {
    return media.value.image_id && media.value.image_id > 0 ? media.value.image_id : undefined
  }

  return data.value?.media
})

const imageUrl = computed(() => {
  if (media.value?.type !== 'image' || !media.value.has_media) return ''
  return cleanString(media.value.image_url) ?? ''
})

const imageAlt = computed(() => cleanString(media.value?.image_alt) ?? '')

const videoFileUrl = computed(() => {
  if (media.value?.type !== 'video' || !media.value.has_media) return ''

  return (
    cleanString(media.value.video?.file_url) ||
    cleanString(media.value.video?.url) ||
    ''
  )
})

const videoEmbedUrl = computed(() => {
  if (media.value?.type !== 'video' || !media.value.has_media) return ''
  return cleanString(media.value.video?.embed_url) ?? ''
})

const hasHeroMedia = computed(() =>
  !!imageId.value ||
  !!imageUrl.value ||
  !!videoFileUrl.value ||
  !!videoEmbedUrl.value,
)
const showHeroSection = computed(() => isAwaitingApiResponse.value || !!data.value)

const showMediaFallback = computed(() => !!data.value && !hasHeroMedia.value)
const heroInnerClasses = computed(() => [
  'container',
  'hero-inner',
  showMediaFallback.value ? 'hero-inner--split' : '',
])

const titleHtml = computed(() => {
  if (settings.value?.hide_title) return ''
  return (hero.value?.title || data.value?.title || '')
})

const supertitleHtml = computed(() => hero.value?.supertitle || '')
const leadHtml = computed(() => hero.value?.introContent || '')
const disclaimerHtml = computed(() => hero.value?.introDisclaimer || '')

const hasSupertitle = computed(() => !!supertitleHtml.value?.trim())
const hasLead = computed(() => !!leadHtml.value?.trim())
const hasDisclaimer = computed(() => !!disclaimerHtml.value?.trim())
const hasTitle = computed(() => !!titleHtml.value?.trim())
const heroIdentityKey = computed(() => String(data.value?.id ?? data.value?.slug ?? 'hero'))
const heroMediaKey = computed(() => [
  heroIdentityKey.value,
  media.value?.type ?? 'none',
  imageId.value ?? '',
  imageUrl.value,
  videoFileUrl.value,
  videoEmbedUrl.value,
].join('::'))

const normalizeSelector = (value?: string | null) => {
  if (!value) return undefined
  const v = value.trim()
  if (!v) return undefined
  // useCta uses querySelector for strings -> CSS selector
  if (v.startsWith('#') || v.startsWith('.')) return v
  return `#${v}`
}

const normalizeInternalPath = (value?: string | null) => {
  if (!value) return undefined
  const v = value.trim()
  if (!v) return undefined
  return v.startsWith('/') ? v : localizedPath('page', { slug: v })
}

const isCtaOptions = (value: CtaOptions | null): value is CtaOptions => value !== null

const ctaOptions = computed<CtaOptions[]>(() => {
  return (hero.value?.ctas ?? []).map((cta): CtaOptions | null => {
    if (!cta.label || !cta.type) return null

    const text = cta.label
    const icon = cta.icon
    const iconPosition = cta.icon_position
    const appearance = cta.appearance
    const color: CtaOptions['color'] = 'primary'

    if (cta.type === 'scroll') {
      const selector = normalizeSelector(cta.scroll_target_uuid)
      if (!selector) return null

      return {
        type: 'scroll',
        text,
        icon,
        iconPosition,
        appearance,
        color,
        targetEl: selector,
      }
    }

    if (cta.type === 'external') {
      const to = cta.external_url || cta.url
      if (!to) return null

      return {
        type: 'external',
        text,
        icon,
        iconPosition,
        appearance,
        color,
        to,
      }
    }

    const to = normalizeInternalPath(cta.url)
    if (!to) return null

    return {
      type: 'internal',
      text,
      icon,
      iconPosition,
      appearance,
      color,
      to,
    }
  }).filter(isCtaOptions)
})

const hasCtas = computed(() => ctaOptions.value.length > 0)

const heroCopyKey = computed(() => [
  heroIdentityKey.value,
  supertitleHtml.value,
  titleHtml.value,
  leadHtml.value,
  disclaimerHtml.value,
  JSON.stringify(ctaOptions.value),
].join('::'))
</script>

<template>
  <section
    v-if="showHeroSection"
    itemscope
    itemprop="primaryImageOfPage"
    :data-id="data?.id"
    :data-type="data?.type"
    :data-layout="hero?.layout ?? settings?.display_mode"
    :data-height="heroHeight"
    :data-size="hero?.size"
    :data-width="hero?.width"
    :class="sectionClasses"
  >
    <Transition name="hero-media" appear>
      <img
        v-if="imageUrl"
        :key="heroMediaKey"
        :src="imageUrl"
        :alt="imageAlt"
        class="hero-image absolute inset-0 -z-10 h-full w-full object-cover"
        loading="eager"
        decoding="async"
      />

      <MediaRenderer
        v-else-if="imageId"
        :key="heroMediaKey"
        :id="imageId"
        imageSize="hero"
        loading="eager"
        class="hero-image absolute inset-0 -z-10"
      />

      <video
        v-else-if="videoFileUrl"
        :key="heroMediaKey"
        class="hero-image absolute inset-0 -z-10 h-full w-full object-cover"
        autoplay
        muted
        loop
        playsinline
        preload="metadata"
      >
        <source :src="videoFileUrl" />
      </video>

      <iframe
        v-else-if="videoEmbedUrl"
        :key="heroMediaKey"
        :src="videoEmbedUrl"
        class="hero-image absolute inset-0 -z-10 h-full w-full"
        title="Hero media"
        loading="eager"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
      />

      <div
        v-else
        :key="`${heroMediaKey}-fallback`"
        class="hero-image absolute inset-0 -z-10 bg-brand-sand/60 dark:bg-base-200"
        aria-hidden="true"
      />
    </Transition>

    <div :class="heroInnerClasses">
      <Transition name="hero-copy" mode="out-in" appear>
        <div
          :key="heroCopyKey"
          class="hero-copy-block hero-text-block"
        >
          <header class="hero-header">
            <div
              v-if="hasSupertitle"
              class="text-sm font-medium text-muted-foreground dark:text-inherit"
            >
              <span v-html="supertitleHtml" />
            </div>

            <h1
              v-if="hasTitle"
              itemprop="headline"
              class="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-balance sm:text-5xl md:text-6xl"
              v-html="titleHtml" />

            <p
              v-if="hasLead"
              itemprop="text"
              class="max-w-xl text-lg leading-relaxed text-muted-foreground text-pretty"
              v-html="leadHtml" />

            <p
              v-if="hasDisclaimer"
              class="max-w-xl text-sm leading-relaxed text-muted-foreground/80 text-pretty"
              v-html="disclaimerHtml" />
          </header>

          <div v-if="hasCtas" class="cta-wrapper">
            <UiCta
              v-for="(cta, index) in ctaOptions"
              :key="`${heroIdentityKey}-cta-${index}`"
              :options="cta"
            />
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>
